import React, { useMemo, useEffect, useState } from 'react';
import { Formik, Form, FieldArray } from 'formik';
import { post } from 'axios';
import PropTypes from 'prop-types';

import { actionSvgs } from 'assets/icons';

import FilledButton from 'components/Buttons/FilledButton';
import ModalContiner from 'components/ModalContainer';

import { renderToast } from 'components/Informations/Toast/utils';

import ErrorMessage from 'components/FormGroup/ErrorMessage';
import SelectField from 'components/FormGroup/SelectField';

import useFetch from 'hooks/useFetch';
import endpoints from 'services/endpoints';

import * as Yup from 'yup';
import * as S from './styles';

const Add = () => actionSvgs('add');
const Delete = () => actionSvgs('remove');

const { getEmployeesToSelect, insertArea, updateArea } = endpoints;

export default function ModalAreas({ data, open, setOpen, type }) {
  const {
    id_funcionario: idFuncionario,
    id_responsavel,
    id_area,
    id_equipe,
    observer,
  } = data;

  const [owners, setOwners] = useState([]);
  const [toastVisibility, setToastVisibility] = useState(false);
  const [toastInfos, setToastInfos] = useState({
    type: '',
    message: '',
    autoClose: 6000,
  });

  const [allEmployees] = useFetch(getEmployeesToSelect);

  useEffect(() => {
    (async function mount() {
      const response = await allEmployees;
      const coordinators = response
        ?.filter(({ nome_funcao: funcao }) =>
          funcao.toLowerCase().includes('coordenador'),
        )
        .map(coordinator => ({
          label: coordinator.nome,
          value: coordinator.id_funcionario,
        }));

      setOwners(coordinators);
    })();

    const unMount = () => {
      setOwners([]);
    };
    return unMount;
  }, [allEmployees]);

  const initialValues = useMemo(() => {
    if (type === 'add') {
      return {
        descricao: '',
        blocos: [''],
      };
    }

    return {
      descricao: data.descricao,
      coordinatorResponsible: id_responsavel ?? '',
    };
  }, [type, data, id_responsavel]);

  const schema = useMemo(() => {
    if (type === 'add') {
      return Yup.object().shape({
        descricao: Yup.string()
          .min(3, 'O nome deve conter no mínimo 3 caractéres')
          .required('Campo obrigatório'),
        coordinatorResponsible: Yup.string().required('Campo obrigatório'),
        blocos: Yup.array().of(Yup.string().required('Campo obrigatório')),
      });
    }

    return Yup.object().shape({
      descricao: Yup.string()
        .min(3, 'O nome deve conter no mínimo 3 caractéres')
        .required('Campo obrigatório'),
      coordinatorResponsible: Yup.string().required('Campo obrigatório'),
    });
  }, [type]);

  const ModalTitle = useMemo(
    () => (type === 'add' ? 'Adicionar nova área' : 'Editar área'),
    [type],
  );

  const handleSubmit = (values, { resetForm }) => {
    if (type === 'add') {
      const objectToSend = {
        id_usr_in: idFuncionario,
        id_usr_up: idFuncionario,
        descricao: values.descricao,
        id_responsavel: values.coordinatorResponsible,
        blocos: values.blocos,
        id_equipe,
      };

      return post(insertArea, objectToSend)
        .then(() => {
          setToastVisibility(true);
          setToastInfos({
            type: 'success',
            message: 'Área inserida com sucesso',
            autoClose: 6000,
          });
        })
        .then(() => {
          setTimeout(() => {
            resetForm();
            observer();
            setOpen(false);
          }, 4000);
        })
        .catch(() => {
          setToastVisibility(true);
          setToastInfos({
            type: 'error',
            message: 'Erro ao adicionar área',
            autoClose: 6000,
          });
        });
    }

    const objectToSend = {
      id_area,
      id_usr_up: idFuncionario,
      descricao: values.descricao,
      id_responsavel: id_responsavel || values.coordinatorResponsible,
    };

    return post(updateArea, objectToSend)
      .then(() => {
        setToastVisibility(true);
        setToastInfos({
          type: 'success',
          message: 'Área atualizada com sucesso',
          autoClose: 6000,
        });
      })
      .then(() => {
        setTimeout(() => {
          resetForm();
          observer();
          setOpen(false);
        }, 4000);
      })
      .catch(() => {
        setToastVisibility(true);
        setToastInfos({
          type: 'error',
          message: 'Erro ao atualizar área',
          autoClose: 6000,
        });
      });
  };

  return (
    <>
      {toastVisibility &&
        renderToast(
          toastInfos.type,
          toastInfos.message,
          toastInfos.autoClose,
          setToastVisibility,
        )}

      <ModalContiner isOpen={open} setIsOpen={setOpen} headText={ModalTitle}>
        <Formik
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, setFieldValue, resetForm }) => (
            <Form>
              <S.FormField
                label="Área"
                label_for="descricao"
                error={!!errors?.descricao}
                error_message={errors?.descricao}
              >
                <S.FormInput
                  value={values.descricao}
                  name="descricao"
                  id="descricao"
                  onChange={e => setFieldValue('descricao', e.target.value)}
                  error={!!errors?.descricao}
                />
              </S.FormField>
              <SelectField
                name="coordinatorResponsible"
                value={owners.find(
                  option =>
                    option.value === values.coordinatorResponsible || '',
                )}
                options={owners}
                handleChange={({ value }) => {
                  setFieldValue('coordinatorResponsible', value);
                }}
                menuPlacement="top"
                hasLabelMargin
                labelText="Gestor responsável"
                placeholderText="Selecione um gestor..."
              />
              {!!errors.coordinatorResponsible && (
                <ErrorMessage message={errors.coordinatorResponsible} />
              )}

              {type === 'add' && (
                <FieldArray
                  name="blocos"
                  render={arrayHelpers =>
                    values.blocos?.map((q, i) => (
                      <S.FormRow
                        error={!!errors.blocos?.length && !!errors.blocos[i]}
                      >
                        <S.FormField
                          label={i === 0 && 'Blocos'}
                          label_for={`questao_${i}_bloco`}
                          error={!!errors.blocos?.length && !!errors.blocos[i]}
                          error_message={
                            !!errors.blocos?.length && errors.blocos[i]
                          }
                        >
                          <S.FormInput
                            value={q}
                            name={`blocos[${i}]`}
                            id={`questao_${i}_bloco`}
                            onChange={e =>
                              setFieldValue(`blocos[${i}]`, e.target.value)
                            }
                            error={!!errors.blocos?.length && errors.blocos[i]}
                            error_message={
                              !!errors.blocos?.length && errors.blocos[i]
                            }
                          />
                        </S.FormField>

                        {values.blocos.length - 1 > 0 && (
                          <S.FormButton
                            className="icon"
                            kind={
                              values.blocos.length - 1 === 0
                                ? 'disbled'
                                : 'cancel'
                            }
                            icon={Delete}
                            disabled={values.blocos.length - 1 === 0}
                            onClick={e => {
                              e.preventDefault();
                              arrayHelpers.remove(i);
                            }}
                          />
                        )}

                        {values.blocos.length - 1 === i && (
                          <S.FormButton
                            className="icon"
                            kind="primary"
                            icon={Add}
                            onClick={e => {
                              e.preventDefault();
                              arrayHelpers.insert(i + 1, '');
                            }}
                          />
                        )}
                      </S.FormRow>
                    ))
                  }
                />
              )}

              <S.ButtonsWrap>
                <FilledButton
                  text="Cancelar"
                  buttonClass="cancel"
                  handleClick={e => {
                    e.preventDefault();
                    resetForm();
                    setOpen(false);
                  }}
                />
                <FilledButton
                  text="enviar"
                  buttonType="submit"
                  buttonClass="confirm"
                />
              </S.ButtonsWrap>
            </Form>
          )}
        </Formik>
      </ModalContiner>
    </>
  );
}

ModalAreas.defaultProps = {
  type: 'add',
  data: {
    nome: '',
    descricao: '',
  },
};
ModalAreas.propTypes = {
  data: PropTypes.shape({
    id_area: PropTypes.number.isRequired,
    nome: PropTypes.string,
    descricao: PropTypes.string,
  }),
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  type: PropTypes.string,
};
